// @flow
import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import debounce from 'lodash.debounce';
import {
  Card,
  Table,
  Row,
  Col,
  Tooltip,
  Badge,
  Spin,
  Icon,
} from 'antd';
import {
  SpButton,
  FilterWrapper,
  SpText,
} from 'components/DesignKit';
import {
  axiosAuthInstance,
  API_END_POINTS,
} from 'api';
import {
  notificationActions,
  filterParamsActions,
  affiliateEarningsActions,
} from 'store/actions';
import { NOTIFICATION, FILTERS_SPECS } from 'store/actionTypes';
import queryString from 'query-string';
import { useLocation } from 'react-router-dom';
import { CSVLink } from 'react-csv';
import moment from 'moment-timezone';

import Loading from 'components/Loading';
import Filters from 'components/Filters';
import ExportIcon from 'components/NavIcons/exportIcon';
import FilterIcon from 'components/NavIcons/filterIcon';
import Download from 'components/NavIcons/downloadIcon';

import getFilterOperators from 'utils/getFilterOperators';
import formatNumber from 'utils/formatNumber';

import {
  PAGES,
  ROLES,
  FILTERS_TYPES,
  MESSAGES,
} from 'appconstants';

import RBAC from 'components/rbac';

const MOMENT_FORMAT = 'YYYY-MM-DDTHH:mm:ss';
const EARNINGS_FILTERS_TYPES = FILTERS_TYPES.PARTNER_EARNINGS;

type Props = {
  accounts: {
    content: Array<Object>
  },
  fetchAffiliateEarnings: Function,
  affiliateEarnings: Array<Object>,
  loading: boolean,
  test: boolean,
  totalElements: number,
  history: {
    push: Function,
  },
  selectedAccount: Object,
  setNotification: Function,
  setFilterData: Function,
  preserveFilters: Object,
};

const EarningsList = (props: Props) => {
  const myRef = useRef();
  const [pagination, setPagination] = useState({});
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const [count, setCount] = useState(null);
  const {
    loading,
    fetchAffiliateEarnings,
    totalElements,
    affiliateEarnings,
    test,
    history,
    selectedAccount,
    setNotification,
    setFilterData,
    preserveFilters,
  } = props;
  const { pathname } = location;
  const [fillColor, setFillColor] = useState(false);
  const [filterColor, setFilterColor] = useState(false);
  const [filterEarnings, setfilterEarnings] = useState((preserveFilters && preserveFilters.filterEarnings)
    ? (preserveFilters && preserveFilters.filterEarnings)
    : [{
      filterName: '',
      filterOperator: '',
      filterValue: '',
      filterNameDisplay: '',
      checked: true,
    }]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentDate, setCurrentDate] = useState('');
  const [filterTypes, setFilterTypes] = useState(EARNINGS_FILTERS_TYPES);
  const [fiterOperators, setFilterOperators] = useState([]);
  const [loader, setLoader] = useState(false);
  const currencyType = selectedAccount && selectedAccount.currency;
  const preserveParam = ((preserveFilters && preserveFilters.pathname) === pathname)
    ? queryString.parse(preserveFilters.search)
    : setFilterData({
      pathname,
      search: '',
      filterEarnings: [{
        filterName: '',
        filterOperator: '',
        filterValue: '',
        filterNameDisplay: '',
        checked: true,
      }],
    });

  const debouncedfetchAffiliateEarnings = debounce(fetchAffiliateEarnings, 2000, { leading: true });

  const getData = (query) => {
    const parsed = queryString.parse(query);
    const currentPage = parsed.pageNum || 1;
    const sortBy = parsed.sortBy || 'createdAt';
    const direction = parsed.direction || 'DESC';
    const sortParams = `&sortBy=${sortBy}&direction=${direction}`;
    const filterParam = parsed.filterParams || '';
    const filterParams = (preserveParam ? preserveParam.filterParams : filterParam || '');
    debouncedfetchAffiliateEarnings({ currentPage, sortParams, filterParams });
  };

  const exportData = affiliateEarnings ? affiliateEarnings.map((values) => ({
    totalAccounts: values.partnerEarnings.totalAccounts,
    totalTransactions: values.partnerEarnings.totalTransactions,
    totalTransactionAmount: `${currencyType.prefix}${formatNumber((values.partnerEarnings.totalTransactionAmount / 100).toFixed(2), currencyType)}`,
    totalFeeCollected: `${currencyType.prefix}${formatNumber((values.partnerEarnings.totalFeesCollected / 100).toFixed(2), currencyType)}`,
    totalCommisionCollected: `${currencyType.prefix}${formatNumber((values.partnerEarnings.totalFeesCollected / 100).toFixed(2), currencyType)}`,
    createdAt: moment
      .utc(values.createdAt)
      .tz(selectedAccount.timezone)
      .format('MMM DD, YYYY, hh:mm a'),
  })) : '';

  const exportHeaders = [
    { label: 'TOTAL ACCOUNTS', key: 'totalAccounts' },
    { label: 'TOTAL TRANSACTIONS', key: 'totalTransactions' },
    { label: 'TRANSACTION AMOUNT', key: 'totalTransactionAmount' },
    { label: 'TOTAL FEES COLLECTED', key: 'totalFeeCollected' },
    { label: 'TOTAL COMMISSION COLLECTED', key: 'totalCommisionCollected' },
    { label: 'DATE', key: 'createdAt' },
  ];

  useEffect(() => {
    getData(location.search);
  }, [test, selectedAccount, location]);

  useEffect(() => {
    const parsed = queryString.parse(location.search);
    const currentPage = parsed.pageNum || 1;
    setPagination(existingElements => ({
      ...existingElements,
      total: totalElements,
      current: parseInt(currentPage, 10),
      showTotal: total => `Total ${total} items`,
    }));
  }, [affiliateEarnings]);

  const handleClick = e => {
    if (myRef.current && myRef.current.contains(e.target)) {
      // inside click
      return;
    }
    if (e.target.value === undefined
      && e.toElement.className !== 'ant-calendar-date'
      && e.toElement.className !== 'ant-calendar-prev-month-btn'
      && e.toElement.className !== 'ant-calendar-next-month-btn') {
      // Outside click
      setOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  }, []);

  const resetFilters = () => {
    setFilterData({
      pathname,
      search: '',
    });
    setfilterEarnings([{
      filterType: '',
      filterName: '',
      filterOperator: '',
      filterValue: '',
      filterNameDisplay: '',
      checked: true,
    }]);
    setFilterTypes(EARNINGS_FILTERS_TYPES);
    setStartDate('');
    setEndDate('');
    setCurrentDate('');
    setCount(null);
    history.push(PAGES.PARTNER_EARNINGS);
  };

  const handleTableChange = (page, sorter, filters) => {
    const parsed = queryString.parse(location.search);
    const filterParams = parsed.filterParams || '';
    const tempPage = page.current;
    let tempParams = '';
    if (filters.order === 'ascend') {
      tempParams = `&sortBy=${filters.field}&direction=ASC`;
    } else if (filters.order === 'descend') {
      tempParams = `&sortBy=${filters.field}&direction=DESC`;
    } else {
      tempParams = '';
    }
    if (location.search && filterParams) {
      const filterParam = encodeURIComponent(filterParams);
      history.push({
        pathname: PAGES.PARTNER_EARNINGS,
        search: `?filterParams=${filterParam}&pageNum=${tempPage}${tempParams}`,
      });
    } else {
      history.push({
        pathname: PAGES.PARTNER_EARNINGS,
        search: `?pageNum=${tempPage}${tempParams}`,
      });
    }
  };

  const onCheck = (e, index) => {
    const list = [...filterEarnings];
    if (filterEarnings.length !== 1) {
      if (!e) {
        list.splice(index, 1);
        const filterTypeOptions = EARNINGS_FILTERS_TYPES.filter(({ value: id1 }) => !list.some(({ filterName: id2 }) => id2 === id1));
        setFilterTypes(filterTypeOptions);
      } else {
        list[index].checked = e;
      }
    }
    setfilterEarnings(list);
  };

  const handleChange = (e, index) => {
    const list = [...filterEarnings];
    if (e[1] === 'createdAt') {
      list[index].filterType = 'Date';
    } else if (e[1] === 'amount') {
      list[index].filterType = 'Number';
    } else if (e[1] === 'partnerEarnings.totalTransactionAmount') {
      list[index].filterType = 'Number';
    } else if (e[1] === 'partnerEarnings.totalFeesCollected') {
      list[index].filterType = 'Number';
    } else if (e[1] === 'partnerEarnings.totalAccounts') {
      list[index].filterType = 'Number';
    } else if (e[1] === 'partnerEarnings.totalTransactions') {
      list[index].filterType = 'Number';
    } else {
      list[index].filterType = 'String';
    }
    list[index].filterName = e[1];
    list[index].filterNameDisplay = e[0];
    list[index].filterOperator = '';
    list[index].filterValue = '';
    const filterTypeOptions = EARNINGS_FILTERS_TYPES.filter(({ value: id1 }) => !list.some(({ filterName: id2 }) => id2 === id1));
    setfilterEarnings(list);
    setFilterTypes(filterTypeOptions);
    setFilterOperators(getFilterOperators(e[1]));
  };

  const handleFromValue = (e, index) => {
    const list = [...filterEarnings];
    const regex = /^\d*\.?\d*$/;
    if (e.currentTarget && regex.test(e.currentTarget.value.replace(/,/g, ''))) {
      list[index].filterValueFrom = e.currentTarget.value;
      setfilterEarnings(list);
    } else if (regex.test(e.replace(/,/g, ''))) {
      list[index].filterValueFrom = e;
      setfilterEarnings(list);
    }
  };

  const handleToValue = (e, index) => {
    const list = [...filterEarnings];
    const regex = /^\d*\.?\d*$/;
    if (e.currentTarget && regex.test(e.currentTarget.value.replace(/,/g, ''))) {
      list[index].filterValueTo = e.currentTarget.value;
      setfilterEarnings(list);
    } else if (regex.test(e.replace(/,/g, ''))) {
      list[index].filterValueTo = e;
      setfilterEarnings(list);
    }
  };

  const handleValue = (e, index) => {
    let selectedValue = '';
    if (filterEarnings[index].filterName === 'createdAt') {
      if (filterEarnings[index].filterOperator === 'BT') {
        const date1 = e[0];
        const date2 = e[1];
        const utcDate1 = moment(date1).startOf('day');
        setStartDate(utcDate1);
        const utcDate2 = moment(date2).startOf('day');
        setEndDate(utcDate2);
        const utcString1 = utcDate1.format(MOMENT_FORMAT);
        const utcString2 = utcDate2.format(MOMENT_FORMAT);
        const selectedDate1 = moment.tz(utcString1, selectedAccount.timezone);
        const selectedDate2 = moment.tz(utcString2, selectedAccount.timezone);
        const dateStart = selectedDate1.utc().format(MOMENT_FORMAT);
        const dateEnd = selectedDate2.utc().format(MOMENT_FORMAT);
        selectedValue = `createdAt.GT=${dateStart}&createdAt.LT=${dateEnd}`;
      } else if (filterEarnings[index].filterOperator === 'LT') {
        const utcDate = moment(e).startOf('day');
        setCurrentDate(utcDate);
        const utcString = utcDate.format(MOMENT_FORMAT);
        const dateSelected = moment.tz(utcString, selectedAccount.timezone);
        const dateStart = dateSelected.utc().format(MOMENT_FORMAT);
        selectedValue = `createdAt.LT=${dateStart}`;
      } else if (filterEarnings[index].filterOperator === 'GT') {
        const utcDate = moment(e).startOf('day');
        setCurrentDate(utcDate);
        const utcString = utcDate.format(MOMENT_FORMAT);
        const dateSelected = moment.tz(utcString, selectedAccount.timezone);
        const dateStart = dateSelected.utc().format(MOMENT_FORMAT);
        selectedValue = `createdAt.GT=${dateStart}`;
      }
    } else {
      if (!e.currentTarget) {
        selectedValue = e;
      }
      if (e.currentTarget && e.currentTarget.value) {
        selectedValue = e.currentTarget.value;
      }
    }
    const list = [...filterEarnings];
    list[index].filterValue = selectedValue;
    setfilterEarnings(list);
  };

  const handleOperator = (e, index) => {
    const list = [...filterEarnings];
    list[index].filterOperator = e;
    if (list[index].filterValue) {
      if (list[index].filterOperator !== 'BT'
        && list[index].filterName === 'createdAt') {
        handleValue(currentDate, index);
      } else if (list[index].filterOperator === 'BT'
        && list[index].filterName === 'createdAt') {
        const dateList = [startDate, endDate];
        handleValue(dateList, index);
      } else if (list[index].filterOperator !== 'BT'
        && list[index].filterOperator !== 'LT'
        && list[index].filterOperator !== 'GT') {
        const value = filterEarnings[index].filterValue;
        handleValue(value, index);
      }
    }
    setfilterEarnings(list);
  };


  const handleAddClick = () => {
    const list = [...filterEarnings];
    setfilterEarnings([...list, {
      filterType: '',
      filterName: '',
      filterOperator: '',
      filterValue: '',
      filterNameDisplay: '',
      checked: true,
    }]);
  };

  const applyFilters = () => {
    const earningsFilters = filterEarnings.filter((item) => item.checked);
    for (let i = 0; i < earningsFilters.length; i += 1) {
      if (!earningsFilters[i].filterName || !earningsFilters[i].filterOperator || !earningsFilters[i].filterValue) {
        return;
      }
    }
    if (earningsFilters[0].filterValue || (earningsFilters[0].filterValueFrom && earningsFilters[0].filterValueTo)) {
      setCount(earningsFilters && earningsFilters.length);
      for (let i = 0; i < earningsFilters.length; i += 1) {
        if (earningsFilters[i].checked) {
          if (earningsFilters[i].filterName === 'createdAt') {
            earningsFilters[i] = earningsFilters[i].filterValue;
          } else if (earningsFilters[i].filterName === 'amount' && earningsFilters[i].filterOperator === 'BT') {
            const name = earningsFilters[i].filterName;
            const from = earningsFilters[i].filterValueFrom.replace(/[. ,:-]+/g, '');
            const to = earningsFilters[i].filterValueTo.replace(/[. ,:-]+/g, '');
            earningsFilters[i] = `${name}.GT=${from}&${name}.LT=${to}`;
          } else if (earningsFilters[i].filterName === 'amount' && earningsFilters[i].filterOperator !== 'BT') {
            const value = earningsFilters[i].filterValue.replace(/[. ,:-]+/g, '');
            earningsFilters[i] = `${earningsFilters[i].filterName}.${earningsFilters[i].filterOperator}=${value}`;
          } else if (earningsFilters[i].filterName === 'partnerEarnings.totalTransactionAmount' && earningsFilters[i].filterOperator === 'BT') {
            const name = earningsFilters[i].filterName;
            const from = earningsFilters[i].filterValueFrom.replace(/[. ,:-]+/g, '');
            const to = earningsFilters[i].filterValueTo.replace(/[. ,:-]+/g, '');
            earningsFilters[i] = `${name}.GT=${from}&${name}.LT=${to}`;
          } else if (earningsFilters[i].filterName === 'partnerEarnings.totalTransactionAmount' && earningsFilters[i].filterOperator !== 'BT') {
            const value = earningsFilters[i].filterValue.replace(/[. ,:-]+/g, '');
            earningsFilters[i] = `${earningsFilters[i].filterName}.${earningsFilters[i].filterOperator}=${value}`;
          } else if (earningsFilters[i].filterName === 'partnerEarnings.totalFeesCollected' && earningsFilters[i].filterOperator === 'BT') {
            const name = earningsFilters[i].filterName;
            const from = earningsFilters[i].filterValueFrom.replace(/[. ,:-]+/g, '');
            const to = earningsFilters[i].filterValueTo.replace(/[. ,:-]+/g, '');
            earningsFilters[i] = `${name}.GT=${from}&${name}.LT=${to}`;
          } else if (earningsFilters[i].filterName === 'partnerEarnings.totalFeesCollected' && earningsFilters[i].filterOperator !== 'BT') {
            const value = earningsFilters[i].filterValue.replace(/[. ,:-]+/g, '');
            earningsFilters[i] = `${earningsFilters[i].filterName}.${earningsFilters[i].filterOperator}=${value}`;
          } else if (earningsFilters[i].filterName === 'partnerEarnings.totalAccounts' && earningsFilters[i].filterOperator === 'BT') {
            const name = earningsFilters[i].filterName;
            const from = earningsFilters[i].filterValueFrom;
            const to = earningsFilters[i].filterValueTo;
            earningsFilters[i] = `${name}.GT=${from}&${name}.LT=${to}`;
          } else if (earningsFilters[i].filterName === 'partnerEarnings.totalAccounts' && earningsFilters[i].filterOperator !== 'BT') {
            const value = earningsFilters[i].filterValue;
            earningsFilters[i] = `${earningsFilters[i].filterName}.${earningsFilters[i].filterOperator}=${value}`;
          } else if (earningsFilters[i].filterName === 'partnerEarnings.totalTransactions' && earningsFilters[i].filterOperator === 'BT') {
            const name = earningsFilters[i].filterName;
            const from = earningsFilters[i].filterValueFrom;
            const to = earningsFilters[i].filterValueTo;
            earningsFilters[i] = `${name}.GT=${from}&${name}.LT=${to}`;
          } else if (earningsFilters[i].filterName === 'partnerEarnings.totalTransactions' && earningsFilters[i].filterOperator !== 'BT') {
            const value = earningsFilters[i].filterValue;
            earningsFilters[i] = `${earningsFilters[i].filterName}.${earningsFilters[i].filterOperator}=${value}`;
          } else {
            earningsFilters[i] = `${earningsFilters[i].filterName}.${earningsFilters[i].filterOperator}=${earningsFilters[i].filterValue}`;
          }
        }
      }

      const filterParams = encodeURIComponent(earningsFilters.join('&'));
      const parsed = queryString.parse(location.search);
      const currentPage = 1;
      const sortBy = parsed.sortBy || 'createdAt';
      const direction = parsed.direction || 'DESC';
      const sortParams = `&sortBy=${sortBy}&direction=${direction}`;
      if (location.search && parsed.sortBy && parsed.pageNum && parsed.direction) {
        setFilterData({
          pathname,
          search: `?filterParams=${filterParams}&pageNum=${currentPage}${sortParams}`,
          filterEarnings,
        });
        history.push({
          pathname: PAGES.PARTNER_EARNINGS,
          search: `?filterParams=${filterParams}&pageNum=${currentPage}${sortParams}`,
        });
      } else {
        setFilterData({
          pathname,
          search: `?filterParams=${filterParams}`,
          filterEarnings,
        });
        history.push({
          pathname: PAGES.PARTNER_EARNINGS,
          search: `?filterParams=${filterParams}`,
        });
      }
      setOpen(false);
    }
  };

  const invoiceDownload = async (record) => {
    setLoader(true);
    try {
      const { data: { entity: { fileUrl } } } = await axiosAuthInstance
        .get(`${API_END_POINTS.AFFILIATE_EARNINGS}/${record.invoiceUrl}/download`);
      window.open(fileUrl);
    } catch (e) {
      setNotification({
        type: NOTIFICATION.ERROR,
        payload: MESSAGES.INVOICE.DOWNLOAD_ERROR,
      });
    } finally {
      setLoader(false);
    }
  };


  const columns = [
    {
      title: 'TOTAL ACCOUNTS',
      dataIndex: 'partnerEarnings.totalAccounts',
      width: '12%',
      align: 'left',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
    },
    {
      title: 'TOTAL TRANSACTIONS',
      dataIndex: 'partnerEarnings.totalTransactions',
      width: '15%',
      align: 'left',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
    },
    {
      title: 'TRANSACTION AMOUNT',
      dataIndex: 'partnerEarnings.totalTransactionAmount',
      width: '15%',
      align: 'left',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (amount) => {
        const total = (amount / 100).toFixed(2);
        return (
          <span> {`${currencyType.prefix} ${formatNumber(total, currencyType)}`}</span>
        );
      },
    },
    {
      title: 'TOTAL FEES COLLECTED',
      dataIndex: 'partnerEarnings.totalFeesCollected',
      width: '15%',
      align: 'left',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (amount) => {
        const total = (amount / 100).toFixed(2);
        return (
          <span> {`${currencyType.prefix} ${formatNumber(total, currencyType)}`}</span>
        );
      },
    },
    {
      title: 'TOTAL COMMISSION COLLECTED',
      dataIndex: 'partnerEarnings.totalCommission',
      width: '20%',
      align: 'left',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (amount) => {
        const total = (amount / 100).toFixed(2);
        return (
          <span> {`${currencyType.prefix} ${formatNumber(total, currencyType)}`}</span>
        );
      },
    },
    {
      title: 'DATE',
      dataIndex: 'createdAt',
      width: '14%',
      align: 'left',
      render: (createdAt) => {
        if (createdAt && selectedAccount && selectedAccount.timezone) {
          const tzDate = moment
            .utc(createdAt)
            .tz(selectedAccount.timezone)
            .format('MMM DD, YYYY');
          return (<span>{tzDate}</span>);
        }
        return (
          <>&#8211;</>
        );
      },
      sorter: true,
      defaultSortOrder: 'descend',
      sortDirections: ['ascend', 'descend', 'ascend'],
    },
    {
      title: 'ACTION',
      dataIndex: 'action',
      width: '14%',
      align: 'center',
      render: (text, record) => (
        <span>
          <Icon
            style={{
              cursor: 'pointer',
            }}
            component={Download}
            onClick={(e) => { e.stopPropagation(); invoiceDownload(record); }}
          />
        </span>
      ),
    },
  ];

  if (loading || !selectedAccount) {
    return <Loading />;
  }

  return (
    <>
      <div className={loader ? 'OVERLAY' : ''}>
        {
          loader && (
            <Spin style={{ width: '100%', height: '100%', transform: 'translateY(50%)' }} />
          )
        }
      </div>
      <Card>
        <Row className="my-2">
          <div className="d-flex justify-content-end">
            <RBAC permit={[ROLES.OWNER, ROLES.ADMINISTRATOR, ROLES.DEVELOPER, ROLES.OPERATIONS, ROLES.SUPERUSER]}>
              <Col>
                <span>
                  {
                    exportData !== null
                      ? (
                        <Tooltip placement="left" title="Click here to export Earnings">
                          <CSVLink
                            target="_blank"
                            className="mr-3"
                            filename="Earnings Report.csv"
                            data={exportData}
                            headers={exportHeaders}
                            onMouseEnter={() => setFillColor(true)}
                            onMouseLeave={() => setFillColor(false)}
                          >
                            <ExportIcon
                              fill={fillColor
                                ? '#279dfe'
                                : '#C0C0C0'}
                            />
                          </CSVLink>
                        </Tooltip>
                      ) : null
                  }
                </span>
                <span ref={myRef}>
                  <span
                    onClick={() => setOpen(!open)}
                    style={{ cursor: 'pointer' }}
                    onMouseEnter={() => setFilterColor(true)}
                    onMouseLeave={() => setFilterColor(false)}
                  >
                    {open && (
                      <Badge count={count} style={{ backgroundColor: '#0090ff' }}>
                        <FilterIcon
                          fill={filterColor
                            ? '#279dfe'
                            : '#C0C0C0'}
                        />
                      </Badge>
                    )}
                    {!open && (
                      <Tooltip placement="left" title="Click here to filter Earnings">
                        <Badge count={count} style={{ backgroundColor: '#0090ff' }}>
                          <FilterIcon
                            fill={filterColor
                              ? '#279dfe'
                              : '#C0C0C0'}
                          />
                        </Badge>
                      </Tooltip>
                    )}
                  </span>
                  {open && (
                    <FilterWrapper
                      style={{ marginLeft: '-262px' }}
                    >
                      <Filters
                        filtersGrid={filterEarnings}
                        filterTypes={filterTypes}
                        handleChange={handleChange}
                        onCheck={onCheck}
                        fiterOperators={fiterOperators}
                        handleOperator={handleOperator}
                        handleValue={handleValue}
                        startDate={startDate}
                        endDate={endDate}
                        currentDate={currentDate}
                        handleFromValue={handleFromValue}
                        handleToValue={handleToValue}
                        fiterText="Enter Value"
                      />
                      <div className="px-4 mt-2">
                        {
                          filterEarnings.length < EARNINGS_FILTERS_TYPES.length && (
                            <div className="mb-2 ml-3" style={{ cursor: 'pointer' }}>
                              <SpText
                                fontWeight="600"
                                color="#279dfe"
                                onClick={handleAddClick}
                              >
                                {'\u002B'}&nbsp; Add another filter
                              </SpText>
                            </div>
                          )
                        }
                      </div>
                      <div className="d-flex mb-2 justify-content-end mr-3">
                        <SpButton
                          type="secondary"
                          shape="round"
                          className="mr-2"
                          ghost
                          onClick={resetFilters}
                        >
                          Clear
                        </SpButton>
                        <SpButton
                          type="primary"
                          shape="round"
                          onClick={applyFilters}
                        >
                          Save
                        </SpButton>
                      </div>
                    </FilterWrapper>
                  )}
                </span>
              </Col>
            </RBAC>
          </div>
        </Row>

        <Table
          columns={columns}
          rowKey={(record) => record.gid}
          dataSource={affiliateEarnings}
          pagination={pagination}
          onChange={handleTableChange}
        />
      </Card>
    </>
  );
};

const mapStateToProps = (state) => ({
  affiliateEarnings: state.affiliateEarnings.content,
  loading: state.loading.loading,
  totalElements: state.affiliateEarnings.totalElements,
  test: state.account.test,
  selectedAccount: state.account.selectedAccount,
  submitting: state.loading.submitting,
  validator: state.loading.validator,
  accounts: state.account.accounts,
  preserveFilters: state.filterParams.preserveFilters,
});

const mapDispatchToProps = (dispatch) => ({
  fetchAffiliateEarnings: param => dispatch(affiliateEarningsActions.fetchAffiliateEarnings({
    payload: param,
  })),
  setNotification: ({ type, payload }) => dispatch(notificationActions.setNotification({
    type,
    payload,
  })),
  setFilterData: (value) => dispatch(filterParamsActions.setFilterData({
    type: FILTERS_SPECS.SET_FILTER_DATA,
    payload: value,
  })),
});

// $FlowFixMe
export default connect(mapStateToProps, mapDispatchToProps)(EarningsList);
